import { ChangeEvent, ReactElement, useState } from 'react';
import { FieldError } from 'react-hook-form';
import { Box, Button, Stack, VStack } from '@chakra-ui/react';
import { AxiosError } from 'axios';
import Joi from 'joi';
import { errorMessage } from 'components/common/Form/ErrorMessages';
import { Separator } from 'components/common/Separator';
import { toastContent } from 'config/toast';
import { axios, caughtErrorGlobally } from 'utils/axios';
import { RegisterCredentialsStep1Dto } from 'types/dto/RegisterCredentialsStep1.dto';
import { useFeatureFlagsContext } from 'hooks/utils/useFeatureFlagContext';
import { RegisterStepProps } from 'pages/auth/Registration';
import { useNovaToast } from 'hooks/utils/useNovaToast';
import { ToastVariant } from 'types/Toast';
import { EmailStatusType, ErrorStatusType } from 'types/StatusTypes';
import { AppleButton } from 'components/atoms/AppleButton';
import { NovaIcon } from 'components/ui/novaIcon';
import { EmailIcon } from 'assets/icons/v2';
import { TermsPrivacyText } from 'components/atoms/TermsPrivacyText';
import { RegistrationLayout } from './RegistrationLayout';
import { GoogleButton } from '../atoms/GoogleButton';
import { Form, RHFFormInput } from '../common/Form/v2';

type RegisterStep1Inputs = {
  email: string;
};

const schema = Joi.object({
  email: Joi.string()
    .email({
      tlds: {
        allow: false,
      },
    })
    .required()
    .messages({
      'string.empty': errorMessage.empty,
      'string.email': errorMessage.email,
    }),
});

export const RegisterStep1 = ({ nextStep, handleChanges }: RegisterStepProps): ReactElement => {
  const toast = useNovaToast();
  const featureFlags = useFeatureFlagsContext();
  const [continueButtonDisabled, setContinueButtonDisabled] = useState(true);

  const handleSubmit = async (registerValues: RegisterCredentialsStep1Dto): Promise<void> => {
    setContinueButtonDisabled(true);
    try {
      const validateMail = await axios.post('/auth/validate', {
        email: registerValues.email,
      });

      setContinueButtonDisabled(false);

      if (validateMail.data === 'USED') {
        toast.show(toastContent.email[EmailStatusType.TAKEN]);
      } else {
        handleChanges('email')({
          target: { value: registerValues.email },
        } as ChangeEvent<HTMLInputElement>);
        nextStep();
      }
    } catch (error) {
      if (caughtErrorGlobally(error as AxiosError)) {
        toast.show(toastContent.error[ErrorStatusType.SERVER_ERROR]);
        return;
      }
      toast.show({
        variant: ToastVariant.ERROR,
      });
    }
  };

  return (
    <RegistrationLayout>
      <VStack w="full">
        <VStack gap="4xl" align="left" w="full">
          <VStack gap="2xl" align="left" w="full">
            <Form<RegisterStep1Inputs, typeof schema>
              onSubmit={handleSubmit}
              schema={schema}
              autoComplete="on"
            >
              {({ formState }) => (
                <VStack gap="2xl" align="left" w="full">
                  <RHFFormInput
                    name="email"
                    error={formState.errors.email as FieldError}
                    placeholder="Email"
                    onChange={(event): void => {
                      event.target.value = event.target.value.toLowerCase();
                      handleChanges('email')(event);
                    }}
                    leftElement={<NovaIcon icon={EmailIcon} size="s" aria-hidden />}
                    clearable
                  />
                  <Stack align="center" w="full">
                    <Button
                      variant="primary"
                      type="submit"
                      w="full"
                      disabled={continueButtonDisabled && !formState.isValid}
                    >
                      Weiter
                    </Button>
                  </Stack>
                </VStack>
              )}
            </Form>
          </VStack>
          <VStack gap="2xl" align="left" w="full">
            <Box width="100%">
              <Separator text="Oder" />
            </Box>
            <VStack gap="m" align="left" w="full">
              {featureFlags.isEnabled('isGoogleAuthEnabled') && (
                <Box width="100%">
                  <GoogleButton />
                </Box>
              )}
              {featureFlags.isEnabled('isAppleAuthEnabled') && (
                <Box width="100%">
                  <AppleButton />
                </Box>
              )}
            </VStack>
            <TermsPrivacyText />
          </VStack>
        </VStack>
      </VStack>
    </RegistrationLayout>
  );
};
